import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { CheckCircle2, Lock } from 'lucide-react';
import { COMPANY } from '../data/mock';
import CardBrands from '../components/CardBrands';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

export default function Checkout() {
  const { items, totalPrice, clear } = useCart();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, percent }
  const [promoError, setPromoError] = useState('');
  const [form, setForm] = useState({
    email: '', firstName: '', lastName: '', address: '', city: '', state: '', zip: '',
    cardName: '', cardNumber: '', exp: '', cvc: '',
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  if (items.length === 0 && !submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        <h1 className="font-serif text-3xl text-slate-900">Nothing to check out</h1>
        <p className="text-slate-600 mt-2">Add a few items to your cart first.</p>
        <Button asChild className="mt-6 bg-[#5d728c] hover:bg-[#4d6278] rounded-full px-7">
          <Link to="/shop-all">Start Shopping</Link>
        </Button>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        <div className="w-20 h-20 rounded-full bg-emerald-50 mx-auto flex items-center justify-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-600" />
        </div>
        <h1 className="font-serif text-3xl text-slate-900 mt-6">Order Confirmed</h1>
        <p className="text-slate-600 mt-2">Thank you! A confirmation has been sent to your email.</p>
        <Button asChild className="mt-8 bg-[#5d728c] hover:bg-[#4d6278] rounded-full px-7">
          <Link to="/">Back to Home</Link>
        </Button>
      </div>
    );
  }

  const onSubmit = (e) => {
    e.preventDefault();
    if (!agreed) {
      toast({
        title: 'Please agree to the Terms',
        description: 'You must agree to the Terms & Conditions and Privacy Policy to continue.',
        variant: 'destructive',
      });
      return;
    }
    // Frontend-only mock checkout
    setSubmitted(true);
    clear();
    toast({ title: 'Order placed', description: 'Thanks for shopping with EC Office Hub!' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // small redirect for nicer UX is not needed because we render confirmation above
    void navigate;
  };

  const subtotal = totalPrice;
  const discount = appliedPromo ? +(subtotal * (appliedPromo.percent / 100)).toFixed(2) : 0;
  const tax = +((subtotal - discount) * 0.0).toFixed(2);
  const total = +(subtotal - discount + tax).toFixed(2);

  const applyPromo = () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) {
      setPromoError('Please enter a promo code.');
      return;
    }
    if (code === 'SAVE20') {
      setAppliedPromo({ code: 'SAVE20', percent: 20 });
      setPromoError('');
      toast({ title: 'Promo applied', description: '20% off has been applied to your order.' });
    } else {
      setAppliedPromo(null);
      setPromoError('Invalid promo code. Try SAVE20 for 20% off.');
    }
  };

  const removePromo = () => {
    setAppliedPromo(null);
    setPromoCode('');
    setPromoError('');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900 mb-8">Checkout</h1>
      <form onSubmit={onSubmit} className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-8">
          {/* Contact */}
          <section className="bg-white border border-slate-200 rounded-lg p-6">
            <h2 className="font-serif text-xl text-slate-900 mb-4">Contact</h2>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={update('email')} className="mt-1" />
            </div>
          </section>

          {/* Shipping */}
          <section className="bg-white border border-slate-200 rounded-lg p-6">
            <h2 className="font-serif text-xl text-slate-900 mb-4">Shipping Address</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first">First Name</Label>
                <Input id="first" required value={form.firstName} onChange={update('firstName')} className="mt-1" />
              </div>
              <div>
                <Label htmlFor="last">Last Name</Label>
                <Input id="last" required value={form.lastName} onChange={update('lastName')} className="mt-1" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="address">Street Address</Label>
                <Input id="address" required value={form.address} onChange={update('address')} className="mt-1" />
              </div>
              <div>
                <Label htmlFor="city">City</Label>
                <Input id="city" required value={form.city} onChange={update('city')} className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="state">State</Label>
                  <Select value={form.state} onValueChange={(v) => setForm((f) => ({ ...f, state: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="State" /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="zip">ZIP</Label>
                  <Input id="zip" required value={form.zip} onChange={update('zip')} className="mt-1" />
                </div>
              </div>
            </div>
          </section>

          {/* Payment */}
          <section className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h2 className="font-serif text-xl text-slate-900">Payment</h2>
              <div className="flex items-center gap-3">
                <CardBrands />
                <span className="text-xs text-slate-500 flex items-center gap-1"><Lock className="w-3.5 h-3.5" /> Secure</span>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label htmlFor="cardName">Name on Card</Label>
                <Input id="cardName" required value={form.cardName} onChange={update('cardName')} className="mt-1" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="cardNumber">Card Number</Label>
                <Input id="cardNumber" required placeholder="1234 5678 9012 3456" value={form.cardNumber} onChange={update('cardNumber')} className="mt-1" />
                <div className="mt-2 flex items-center gap-2">
                  <CardBrands />
                  <span className="text-xs text-slate-500">We accept Visa, Mastercard and Discover.</span>
                </div>
              </div>
              <div>
                <Label htmlFor="exp">Expiration (MM/YY)</Label>
                <Input id="exp" required placeholder="MM/YY" value={form.exp} onChange={update('exp')} className="mt-1" />
              </div>
              <div>
                <Label htmlFor="cvc">CVC</Label>
                <Input id="cvc" required placeholder="123" value={form.cvc} onChange={update('cvc')} className="mt-1" />
              </div>
            </div>
          </section>
        </div>

        {/* Summary */}
        <aside className="bg-slate-50 rounded-lg p-6 h-fit">
          <h3 className="font-serif text-xl text-slate-900 mb-4">Order Summary</h3>
          <ul className="divide-y divide-slate-200">
            {items.map((i) => (
              <li key={i.id} className="py-3 flex gap-3">
                <div className="w-14 h-14 bg-white rounded border border-slate-200 shrink-0">
                  <img src={i.image} alt={i.name} className="w-full h-full object-contain" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-800 line-clamp-2">{i.name}</p>
                  <p className="text-xs text-slate-500">Qty {i.qty}</p>
                </div>
                <p className="text-sm text-slate-900 font-medium">${(i.price * i.qty).toFixed(2)}</p>
              </li>
            ))}
          </ul>

          {/* Promo Code */}
          <div className="mt-4 border border-dashed border-slate-300 rounded-md p-3 bg-white">
            <Label htmlFor="promo" className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Promo Code</Label>
            {appliedPromo ? (
              <div className="mt-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2.5 py-0.5 text-xs font-mono font-semibold">
                    {appliedPromo.code}
                  </span>
                  <span className="text-xs text-emerald-700 font-medium">−{appliedPromo.percent}% applied</span>
                </div>
                <button
                  type="button"
                  onClick={removePromo}
                  className="text-xs text-slate-500 hover:text-slate-800 underline"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="mt-2 flex gap-2">
                <Input
                  id="promo"
                  type="text"
                  placeholder="Enter code (e.g. SAVE20)"
                  value={promoCode}
                  onChange={(e) => { setPromoCode(e.target.value); setPromoError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyPromo(); } }}
                  className="flex-1 uppercase"
                />
                <Button
                  type="button"
                  onClick={applyPromo}
                  className="bg-slate-800 hover:bg-slate-900 rounded-md px-4 text-sm"
                >
                  Apply
                </Button>
              </div>
            )}
            {promoError && <p className="mt-1.5 text-xs text-red-600">{promoError}</p>}
            {!appliedPromo && !promoError && (
              <p className="mt-1.5 text-xs text-slate-500">
                Use code <span className="font-mono font-semibold text-slate-700">SAVE20</span> for 20% off your order.
              </p>
            )}
          </div>

          <dl className="mt-4 space-y-1.5 text-sm">
            <div className="flex justify-between"><dt className="text-slate-600">Subtotal</dt><dd className="text-slate-900">${subtotal.toFixed(2)}</dd></div>
            {appliedPromo && (
              <div className="flex justify-between">
                <dt className="text-slate-600">Discount ({appliedPromo.code} −{appliedPromo.percent}%)</dt>
                <dd className="text-emerald-700">−${discount.toFixed(2)}</dd>
              </div>
            )}
            <div className="flex justify-between"><dt className="text-slate-600">Shipping</dt><dd className="text-emerald-700">Free</dd></div>
            <div className="flex justify-between border-t border-slate-200 pt-2 mt-2"><dt className="text-base text-slate-900 font-semibold">Total</dt><dd className="text-base text-slate-900 font-semibold">${total.toFixed(2)}</dd></div>
          </dl>

          {/* Compliance: Agreement, authorization, descriptor */}
          <div className="mt-6 border border-slate-200 bg-white rounded-lg p-4 space-y-4">
            <label htmlFor="agree-terms" className="flex items-start gap-3 cursor-pointer">
              <input
                id="agree-terms"
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-[#5d728c] focus:ring-[#5d728c] cursor-pointer"
                aria-required="true"
              />
              <span className="text-sm text-slate-800 font-semibold leading-snug">
                I agree to the{' '}
                <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-[#5d728c] underline hover:text-[#4d6278]">
                  Terms &amp; Conditions
                </Link>{' '}
                and{' '}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#5d728c] underline hover:text-[#4d6278]">
                  Privacy Policy
                </Link>
              </span>
            </label>

            <p className="text-xs text-slate-600 leading-relaxed">
              By clicking Complete Checkout, you authorize a one-time charge for the product amount shown above.
              Shipping is free on orders over $0. All orders are delivered by USPS; please allow 2-4 business days for delivery.
            </p>

            <p className="text-xs text-slate-600 leading-relaxed">
              If this product is not for you, please contact customer service at{' '}
              <a href={`tel:${COMPANY.phone}`} className="text-[#5d728c] underline hover:text-[#4d6278]">{COMPANY.phone}</a>{' '}
              or email us at{' '}
              <a href={`mailto:${COMPANY.email}`} className="text-[#5d728c] underline hover:text-[#4d6278]">{COMPANY.email}</a>.
            </p>

            <div className="bg-slate-100 border border-slate-200 rounded-md p-3">
              <p className="text-xs text-slate-800 font-semibold">
                All charges will appear as &quot;8337224069ecofficehub&quot; on your credit card statement.
              </p>
            </div>
          </div>

          <Button
            type="submit"
            disabled={!agreed}
            className="w-full mt-5 bg-[#5d728c] hover:bg-[#4d6278] rounded-full disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Complete Checkout — ${total.toFixed(2)}
          </Button>
        </aside>
      </form>
    </div>
  );
}
